import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { RiMenu2Line, RiShieldUserLine, RiUser3Line } from "react-icons/ri";
import { FaSignOutAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAdminContext } from "../../../context/admin/context/AdminContext";
import { useNavigationContext } from "../../../context/navigation/navigationContext";

// --- Bulletproof Modal Component ---
const LogoutModal = ({ isOpen, onClose, onConfirm, name, role }) => {
  const cancelRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    // Handle Escape key
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);

    // Lock body scroll
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";

    // Auto-focus cancel button for accessibility
    const timeoutId = setTimeout(() => cancelRef.current?.focus(), 50);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalStyle;
      clearTimeout(timeoutId);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      // Added max z-index to guarantee it sits above all other page content
      className="fixed inset-0 z-[999999] flex items-center justify-center p-4 sm:p-6 isolate"
      role="dialog"
      aria-modal="true"
      aria-labelledby="logout-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity animate-in fade-in duration-200"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal Content - Added z-10 so it stays explicitly above the backdrop */}
      <div className="relative z-10 w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-gray-200 animate-in fade-in zoom-in-95 duration-200">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
          <FaSignOutAlt className="h-5 w-5 text-red-600" aria-hidden="true" />
        </div>

        <div className="text-center">
          <h3 id="logout-title" className="text-lg font-bold text-gray-900">
            Sign out of platform?
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-gray-500">
            You are currently signed in as <span className="font-semibold text-gray-700">{name}</span>
            {role && <span className="capitalize"> ({role})</span>}. You will need to enter your credentials to access the portal again.
          </p>
        </div>

        <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            ref={cancelRef}
            onClick={onClose}
            className="w-full justify-center rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 sm:w-auto"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 sm:w-auto"
          >
            <FaSignOutAlt />
            Yes, sign out
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// --- NavBar Component ---
const NavBar = ({ title }) => {
  const { showSideBar, setShowSideBar } = useNavigationContext();
  const { logOut, setSelectedMenu, sysAdmin } = useAdminContext();
  const navigate = useNavigate();

  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Safe destructuring & formatting
  const firstName = sysAdmin?.first_name || "";
  const lastName = sysAdmin?.last_name || "";
  const rawRole = sysAdmin?.role || "";

  const fullName = [firstName, lastName].filter(Boolean).join(" ") || "User";
  const initials = [firstName, lastName].map(n => n?.[0]).filter(Boolean).join("").toUpperCase();
  const prettyRole = rawRole.toLowerCase().replaceAll("_", " ");

  const handleConfirmLogout = () => {
    navigate("/login");
    setSelectedMenu(0);
    logOut();
  };

  return (
    <>
      <header className="sticky top-0 z-40 w-full border-b border-gray-200 bg-white/80 backdrop-blur-md">
        <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">

          {/* Left: Mobile Toggle & Page Title */}
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setShowSideBar(!showSideBar)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 xl:hidden"
              aria-label="Toggle sidebar"
            >
              <RiMenu2Line className="h-5 w-5" />
            </button>

            <div className="flex items-center gap-2.5">
              <span className="hidden h-2 w-2 rounded-full bg-emerald-500 sm:block" aria-hidden="true" />
              <h1 className="text-sm font-bold uppercase tracking-wide text-gray-900 sm:text-base">
                {title || "Dashboard"}
              </h1>
            </div>
          </div>

          {/* Right: User Profile & Actions */}
          <div className="flex items-center gap-4 sm:gap-6">

            {/* User Info (Hidden on very small screens) */}
            <div className="hidden items-center gap-3 sm:flex">
              <div className="flex flex-col text-right">
                <span className="text-[13px] font-bold text-gray-900">{fullName}</span>
                <span className="flex items-center justify-end gap-1 text-[11px] font-medium capitalize text-emerald-600">
                  <RiShieldUserLine className="h-3 w-3" aria-hidden="true" />
                  {prettyRole}
                </span>
              </div>

              {/* Premium Avatar */}
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-sm font-bold text-emerald-700 ring-2 ring-white shadow-sm">
                {initials || <RiUser3Line className="h-5 w-5" />}
              </div>
            </div>

            {/* Subtle Vertical Divider */}
            <div className="hidden h-6 w-px bg-gray-200 sm:block" aria-hidden="true" />

            {/* Sign Out Button */}
            <button
              type="button"
              onClick={() => setShowLogoutModal(true)}
              className="group flex items-center gap-2 rounded-lg p-2 text-sm font-medium text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 sm:px-3 sm:py-2"
              aria-label="Sign out"
            >
              <FaSignOutAlt className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" aria-hidden="true" />
              <span className="hidden sm:block">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <LogoutModal
        isOpen={showLogoutModal}
        name={fullName}
        role={prettyRole}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleConfirmLogout}
      />
    </>
  );
};

export default NavBar;